import { FC, ReactNode } from 'react';

import { useReportComponentDefinitions } from '@/hooks/useReportComponentDefinitions';
import { ReportComponentReadinessOverlay } from '@/shared/components/report-components';
import { ChartSkeleton } from '@/shared/components/skeletons';
import { useDatasetStore } from '@/stores/database/aliases/datasets/store';
import { ComponentUIState } from '@/stores/database/aliases/datasets/types';
import { ReportComponent, ReportComponentType } from '@/types';

import { ResultsView } from '../..';
import { evaluateComponentReadiness } from '../../../../helpers/evaluateComponentReadiness';
import { getReportComponentDatasetQueryStateKey } from '../../../../helpers/hook';

interface ContentProps {
    component: ReportComponent;
    segmented: ResultsView;
}

const Content: FC<ContentProps> = ({ component, segmented }): JSX.Element => {
    const { REPORT_COMPONENT_DEFINITIONS } = useReportComponentDefinitions();

    const definition = REPORT_COMPONENT_DEFINITIONS[component.type];

    const viewMap = {
        [ResultsView.CHART]: definition.renderer({ component }),
    } as Record<ResultsView, ReactNode>;

    const { isReady, missing } = evaluateComponentReadiness(component);
    const queryStateKey = getReportComponentDatasetQueryStateKey(component);
    const queryState = useDatasetStore((state) => state.componentQueryState[queryStateKey]);

    const loading = queryState?.loading;

    const uiState = !isReady
        ? ComponentUIState.NOT_READY
        : loading
          ? ComponentUIState.LOADING
          : ComponentUIState.READY_EMPTY;

    return (
        <>
            <div
                style={{
                    position: 'relative',
                    height: '100%',
                    width: '100%',
                }}
            >
                <div
                    style={{
                        height: '100%',
                        width: '100%',
                        opacity: uiState === ComponentUIState.LOADING ? 0.25 : 1,
                    }}
                >
                    {uiState === ComponentUIState.LOADING ? (
                        <ChartSkeleton />
                    ) : (
                        <div
                            style={{
                                height: '100%',
                                width: '100%',
                            }}
                        >
                            {viewMap[segmented]}
                        </div>
                    )}
                </div>
                {uiState === ComponentUIState.NOT_READY && (
                    <ReportComponentReadinessOverlay
                        appearance={component.type === ReportComponentType.STATISTIC ? 'compact' : 'default'}
                        compactAlignment='center'
                        missing={missing}
                    />
                )}
            </div>
        </>
    );
};

export default Content;
